import { AccessDeniedError } from '@adornis/abac/errors/AccessDeniedError.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { assertIsClientContext, context } from '@adornis/baseql/server/context.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { setUserOnContext } from '@adornis/users/server/baseqlContextHelper.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import * as crypto from 'node:crypto';
import { sendAccountDeletionEmail } from '../../server/utils/email.js';
import { requireUser } from '../authz/access.js';
import { ClassMembership } from '../class-membership.js';
import { ClassRoom } from '../classroom.js';
import { Exam } from '../exam.js';
import { ExamResultRelease } from '../exam-result-release.js';
import { Student } from '../student.js';
import { Teacher } from '../teacher.js';

/**
 * "Konto löschen" (DSGVO Art. 17), confirmed by a link to the account's address — the one check that works for
 * password and Google accounts alike (a Google account's password is random and known to nobody). Clicking
 * "Konto löschen" only mails the link; the deletion runs when the link's page is confirmed. Only the token's hash
 * is stored, as for email verification (email-verification.ts).
 *
 * Teacher and student accounts alike, and accounts with both profiles (db/utils/delete-account.ts). Admin accounts
 * (ADMIN_USER_IDS, startup.ts) are refused: they must be taken out of that list first, so an admin login is never
 * lost by accident.
 */

const TOKEN_TTL_MS = 60 * 60 * 1000;
const RESEND_COOLDOWN_MS = 60 * 1000;

const hashToken = (token: string) => crypto.createHash('sha256').update(token).digest('hex');

const deny = (reason: string) => new AccessDeniedError(`Access denied: ${reason}`, { reason });

/** The account's profiles, and whether it is one of the admins kept out of self-deletion. */
async function loadAccount(userID: string) {
  const [user, teacher, student] = await Promise.all([
    (await getRawCollection(AdornisUser._collectionName)).findOne({ _id: userID as any }, { projection: { roles: 1 } }),
    (await getRawCollection(Teacher._collectionName)).findOne(
      { userID },
      { projection: { _id: 1, firstName: 1, isAdmin: 1 } },
    ),
    (await getRawCollection(Student._collectionName)).findOne({ userID }, { projection: { _id: 1, firstName: 1 } }),
  ]);
  const roles: unknown[] = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = teacher?.isAdmin === true || roles.some(role => (role as { name?: unknown })?.name === 'admin');
  return { teacher, student, isAdmin };
}

/** The profile to greet with and the role to describe, for an account that may be deleted now — or an error. */
async function assertDeletable(userID: string) {
  const { teacher, student, isAdmin } = await loadAccount(userID);
  if (isAdmin) throw deny('admin accounts are kept — remove the account from ADMIN_USER_IDS first');
  const profile = teacher ?? student;
  if (!profile) throw deny('the account has no profile');
  return {
    role: teacher ? ('teacher' as const) : ('student' as const),
    firstName: typeof profile.firstName === 'string' ? profile.firstName : undefined,
  };
}

/** What deleting the caller's account takes with it — shown in the dialog before the link is requested. */
@Entity()
export class AccountDeletionImpactDTO extends AdornisEntity {
  static override _class = 'AccountDeletionImpactDTO';

  @Field(() => Int)
  classes!: number;

  @Field(() => Int)
  exams!: number;

  /** Students who lose a class or released results — memberships of the teacher's classes and their releases. */
  @Field(() => Int)
  students!: number;

  /** 'admin' | 'impersonating' — why the account cannot be deleted from here; null when it can. */
  @Field(() => String)
  blockedReason: Maybe<string>;
}

export const getAccountDeletionImpactQuery = registerQuery({
  operationName: 'getAccountDeletionImpactQuery',
  type: () => AccountDeletionImpactDTO,
  params: [],
  resolve:
    () =>
    async (_selection: BaseQLSelectionSet<AccountDeletionImpactDTO>): Promise<AccountDeletionImpactDTO> => {
      const subject = await requireUser();
      const { teacher, isAdmin } = await loadAccount(subject.userID);

      let classes = 0;
      const studentIDs = new Set<string>();
      if (teacher) {
        const classIDs = (
          await (await getRawCollection(ClassRoom._collectionName))
            .find({ teacherID: teacher._id }, { projection: { _id: 1 } })
            .toArray()
        ).map(classroom => classroom._id);
        classes = classIDs.length;
        const memberships = await (await getRawCollection(ClassMembership._collectionName))
          .find(
            { classRoomID: { $in: classIDs }, status: { $in: ['pending', 'approved'] } },
            { projection: { studentID: 1 } },
          )
          .toArray();
        const releases = await (await getRawCollection(ExamResultRelease._collectionName))
          .find({ releasedBy: teacher._id }, { projection: { studentID: 1 } })
          .toArray();
        for (const row of [...memberships, ...releases]) if (row.studentID) studentIDs.add(String(row.studentID));
      }
      const exams = await (await getRawCollection(Exam._collectionName)).countDocuments({ userID: subject.userID });

      return new AccountDeletionImpactDTO({
        classes,
        exams,
        students: studentIDs.size,
        blockedReason: subject.impersonatingUserID ? 'impersonating' : isAdmin ? 'admin' : null,
      });
    },
});

export const requestAccountDeletionMutation = registerMutation({
  operationName: 'requestAccountDeletionMutation',
  type: () => Boolean,
  params: [],
  resolve: () => async () => {
    const subject = await requireUser();
    // An admin acting as the user must not be able to delete the account.
    if (subject.impersonatingUserID) throw deny('not while impersonating');
    const account = await assertDeletable(subject.userID);

    const users = await getRawCollection(AdornisUser._collectionName);
    const user = await users.findOne(
      { _id: subject.userID as any },
      { projection: { _id: 1, email: 1, accountDeletion: 1 } },
    );
    if (typeof user?.email !== 'string' || !user.email) throw deny('the account has no address to confirm with');

    // A double click or a second tab does not send a second mail (and void the first link).
    const sentAt = user.accountDeletion?.sentAt;
    if (sentAt instanceof Date && Date.now() - sentAt.getTime() < RESEND_COOLDOWN_MS) return true;

    const token = crypto.randomBytes(32).toString('base64url');
    const now = new Date();
    await users.updateOne(
      { _id: user._id },
      {
        $set: {
          accountDeletion: {
            tokenHash: hashToken(token),
            expiresAt: new Date(now.getTime() + TOKEN_TTL_MS),
            sentAt: now,
          },
        },
      },
    );

    const deleteLink = new URL('/auth/delete-account', baseConfig.get('ROOT_URL'));
    deleteLink.searchParams.set('token', token);
    await sendAccountDeletionEmail(user.email, deleteLink.toString(), subject.userID, account.firstName, account.role);
    return true;
  },
});

export const confirmAccountDeletionMutation = registerMutation({
  operationName: 'confirmAccountDeletionMutation',
  type: () => Boolean,
  params: [{ name: 'token', type: () => String }],
  resolve: (token: string) => async () => {
    assertIsClientContext(context);
    if (context.impersonatingUser) throw deny('not while impersonating');

    const users = await getRawCollection(AdornisUser._collectionName);
    const user =
      typeof token === 'string' && token
        ? await users.findOne(
            { 'accountDeletion.tokenHash': hashToken(token), 'accountDeletion.expiresAt': { $gt: new Date() } },
            { projection: { _id: 1, _class: 1 } },
          )
        : null;
    if (!user) {
      throw new ExpectedError('Account deletion link is invalid or expired', {
        translationKey: 'users.account_deletion_invalid',
      });
    }
    const userID = String(user._id);
    await assertDeletable(userID);

    // Opening the link proves control of the mailbox, as for a password reset. The deletion runs as the user: the
    // exam cascade only deletes files the caller owns.
    await setUserOnContext({ _id: user._id, _class: user._class ?? AdornisUser._class } as any);

    // Server only (node:fs, the job store); this module also reaches the client bundle.
    const { deleteAccount } = await import('../utils/delete-account.js');
    await deleteAccount(userID);

    // Sign this session out too — what @adornis/users' downGradeSession does.
    await setUserOnContext({ _id: undefined, _class: undefined } as any);
    return true;
  },
});
